import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { addDoc, collection, doc, getDoc, getDocs, increment, query, updateDoc, where , serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Bar } from 'react-chartjs-2';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import {
  ArrowLeft,
  BookOpen,
  Bot,
  Brain,
  FilePlus,
  Loader2,
  LogIn,
  MessageCircle,
  Send,
  Sparkles,
} from 'lucide-react';

import { auth, db, functions } from '../firebase';
import { trackClick } from './trackClick';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const quickPrompts = [
  { label: 'How to Generate a Worksheet?', text: 'How to Generate a Worksheet?', icon: MessageCircle,answer:'Login--->Create Profile(If Logging in first time)--->Click on Create Worksheet --->Choose Subject--->Choose Difficulty level--->Choose Lesson and/or Topic--->Click on Generate Worksheet or give custom Instructions--->Wait for File to Generate--->Download as Pdf--->Upload if found useful for other Parents.\n\n  Watch this Help video for more details : https://www.youtube.com/embed/ytTHnwdIl4A' },
  { label: 'How to take a quiz?', text: 'How to take a quiz?', icon: MessageCircle ,answer:'Login--->Create Profile(If Logging in first time)--->Click on Solve Quiz --->Choose Subject--->Choose Format--->Choose Lesson and/or Topic--->Click on Generate Quiz--->Wait for Quiz to Load--->Take Quiz--->Retake Same or Similar Quiz.\n\n  Watch this Help video for more details : https://www.youtube.com/embed/PIaPP-eZGOo?si=N6gp1W-typR_S120' },
  { label: 'How to Write a Composition?', text: 'How to Write a Composition?', icon: MessageCircle ,answer:'Login--->Create Profile(If Logging in first time)--->Click on Composition --->Choose Language--->Enter Topic--->Click on Generate Ideas or Sentences--->\n\n  Watch this Help video for more details : https://www.youtube.com/embed/fUGbIas_KVw' },
  { label: 'How to Practice a Comprehension?', text: 'How to Practice a Comprehension?.', icon: MessageCircle ,answer:'Login--->Create Profile(If Logging in first time)--->Click on Comprehension --->Choose Language--->Click on Generate--->Wait for File to Generate--->Download as Pdf.\n\n  Watch this Help video for more details : https://www.youtube.com/embed/8zAO_HCfU-4' },
  { label: 'How to Discuss a Chapter?', text: 'How to Discuss a Chapter?', icon: MessageCircle ,answer:'Login--->Create Profile(If Logging in first time)--->Click on Discuss a Chapter--->Choose Subject--->Choose Lesson--->Click on New Chat--->Enter your query' },
  { label: 'How to Add a Lesson?', text: 'How to Add a Lesson?', icon: MessageCircle ,answer:'Login--->Create Profile(If Logging in first time)--->Click on Add a Lesson --->Choose Language--->Choose Method Image or Typing--->Enter Text or Click/Upload Images of Chapter pages--->Click on Read Images--->Wait for Image Processing--->Click Associate with a lesson--->Enter Subject and Chapter--->Click Share with Parents---->Click Submit.\n\n  Watch this Help video for more details : https://www.youtube.com/embed/igW9f5nbHm4' },
  { label: 'How to Check My Performance?', text: 'How to Check My Performance?', icon: MessageCircle, showPerformanceReport: true, answer:'Login--->Create Profile(If Logging in first time)--->Click on Easy icon --->Ask Question in chat. Once signed in, clicking this question will show your actual quiz and online worksheet performance inside Easy.' },
  { label: 'How to Change my Eascode Language?', text: 'How to Change my Eascode Language?', icon: MessageCircle ,answer:'Login--->Create Profile(If Logging in first time)--->Click on Laguage Selector on top of Screen.' },
];

const toolRedirects = {
  worksheet: {
    path: '/Eascode_Agent_Lesson',
    label: 'worksheet utility',
    message: 'I cannot create worksheets inside this chat. I will open the worksheet utility for you.',
  },
  quiz: {
    path: '/AIQuestionTest',
    label: 'quiz utility',
    message: 'I cannot create quiz questions inside this chat. I will open the quiz utility for you.',
  },
  chapter: {
    path: '/ChapterChat',
    label: 'chapter discussion utility',
    message: 'I cannot discuss chapter content inside this chat. I will open the chapter discussion utility for you.',
  },
};

const REDIRECT_DELAY_SECONDS = 3;

const getRedirectMessage = (redirectIntent, secondsLeft = REDIRECT_DELAY_SECONDS) => {
  if (secondsLeft <= 0) {
    return `${redirectIntent.message}\n\nOpening ${redirectIntent.label} now...`;
  }

  return `${redirectIntent.message}\n\nOpening ${redirectIntent.label} in ${secondsLeft} second${secondsLeft === 1 ? '' : 's'}...`;
};

const initialMessages = [
  {
    id: 'welcome',
    role: 'assistant',
    text: 'Hi, I am Easy your personal AI Study Partner. Please select any question from the list or login to ask any question related to your course from me .',
  },
];

const CHAT_SESSION_KEY = 'eascode_chat_last_messages';
const CHAT_FIRESTORE_SESSION_KEY = 'eascode_chat_firestore_session_id';

const loadSessionMessages = () => {
  try {
    const savedMessages = JSON.parse(sessionStorage.getItem(CHAT_SESSION_KEY) || '[]');
    return Array.isArray(savedMessages) && savedMessages.length > 0
      ? savedMessages
      : initialMessages;
  } catch (error) {
    console.error('Error loading Eascode chat session:', error);
    return initialMessages;
  }
};

const getSerializableMessage = message => ({
  id: message.id,
  role: message.role,
  text: message.text,
  performanceReport: message.performanceReport || null,
  comparisonReport: message.comparisonReport || null,
  resourceSuggestions: message.resourceSuggestions || null,
});

const getYoutubeEmbedUrl = text => {
  const match = (text || '').match(/https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\/[^\s)]+/i);
  if (!match) return '';

  try {
    const url = new URL(match[0].replace(/[.,]+$/, ''));
    const host = url.hostname.replace(/^www\./, '');
    let videoId = '';

    if (host === 'youtu.be') {
      videoId = url.pathname.split('/').filter(Boolean)[0] || '';
    } else if (host === 'youtube.com') {
      const parts = url.pathname.split('/').filter(Boolean);
      if (parts[0] === 'embed' || parts[0] === 'shorts') {
        videoId = parts[1] || '';
      } else if (url.pathname === '/watch') {
        videoId = url.searchParams.get('v') || '';
      }
    }

    return /^[A-Za-z0-9_-]{11}$/.test(videoId)
      ? `https://www.youtube.com/embed/${videoId}`
      : '';
  } catch (error) {
    console.error('Invalid YouTube URL:', error);
    return '';
  }
};

const normalizeText = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const normalizeSubjectText = value =>
  normalizeText(value)
    .replace(/\bmathematics\b/g, 'math')
    .replace(/\bmaths\b/g, 'math');

const isToolUsageQuestion = text =>
  /\b(how|where|steps|guide|tutorial|help|use|using|video|instructions)\b/i.test(text || '');

const getToolRedirectIntent = text => {
  if (isToolUsageQuestion(text)) return null;

  const question = text || '';
  const actionIntent = /\b(create|generate|make|prepare|build|give|need|want|practice|take|start|open|launch|begin|set up)\b/i.test(question);
  const worksheetIntent = /\b(worksheet|practice sheet|exercise sheet|assignment)\b/i.test(question);
  const quizIntent = /\b(quiz|test|mcq|questions?)\b/i.test(question);
  const learningIntent =
    /\b(learn|study|explore|read|revise)\b/i.test(question) &&
    /\b(about|chapter|lesson|topic|concept|subject|content)\b/i.test(question) &&
    !/\b(eascode|easy|app|website|platform|tool|utility)\b/i.test(question);
  const chapterIntent =
    learningIntent ||
    (
      /\b(explain|discuss|teach|summarize|summary|doubt|question|answer|solve|understand|chapter content)\b/i.test(question) &&
      /\b(chapter|lesson|topic|concept|content)\b/i.test(question)
    );

  if (actionIntent && worksheetIntent) return toolRedirects.worksheet;
  if (actionIntent && quizIntent) return toolRedirects.quiz;
  if (chapterIntent) return toolRedirects.chapter;

  return null;
};

const toNumber = value => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const toDate = timestamp => {
  if (!timestamp) return null;
  if (typeof timestamp.toDate === 'function') return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;

  const parsed = new Date(timestamp);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getTimeValue = timestamp => toDate(timestamp)?.getTime() || 0;

const formatDateLabel = timestamp => {
  const date = toDate(timestamp);
  if (!date) return 'Date not available';

  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatTopicValue = value => {
  if (Array.isArray(value?.current)) return value.current.join(', ');
  if (Array.isArray(value)) return value.join(', ');
  return String(value || 'Not Available');
};

const getDeviceType = () => {
  if (typeof navigator === 'undefined') return 'Unknown';
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop';
};

const round1 = value => Math.round((value || 0) * 10) / 10;

const formatScore = row => {
  const score = toNumber(row.score);
  const totalQuestions = toNumber(row.totalQuestions || row.maxQuestions || 20);
  const safeTotal = totalQuestions > 0 ? totalQuestions : 20;

  return {
    score,
    total: safeTotal,
    percent: safeTotal > 0 ? (score / safeTotal) * 100 : 0,
  };
};

const normalizeOnlineWorksheetResult = row => {
  const lessonDetails = row.lessonDetails || {};
  const totalQuestions =
    toNumber(row.totalQuestions) ||
    toNumber(row.correctCount) + toNumber(row.incorrectCount);

  return {
    ...row,
    subject: lessonDetails.subject || row.subject || 'Unknown Subject',
    lesson: lessonDetails.lessonName || row.lessonName || 'Unknown Chapter',
    topic: formatTopicValue(lessonDetails.topics || row.topics),
    school: lessonDetails.school || row.school || '',
    standard: lessonDetails.standard || row.standard || '',
    score: toNumber(row.correctCount),
    totalQuestions,
    timestamp: row.datetimeOfTest,
    questions: Array.isArray(row.questions) ? row.questions : [],
  };
};

const buildOnlineWorksheetPerformance = (worksheetResults, requestedSubject = '') => {
  const scopedResults = requestedSubject
    ? worksheetResults.filter(row => normalizeSubjectText(row.subject) === normalizeSubjectText(requestedSubject))
    : worksheetResults;

  if (scopedResults.length === 0) return null;

  const totals = scopedResults.reduce(
    (acc, row) => {
      acc.correct += toNumber(row.score);
      acc.total += toNumber(row.totalQuestions);
      return acc;
    },
    { correct: 0, total: 0 }
  );
  const subjectGroups = {};
  const lessonTopicGroups = {};
  const questionTypeGroups = {};

  scopedResults.forEach(row => {
    const subjectLabel = row.subject || 'Unknown Subject';
    addToGroup(subjectGroups, subjectLabel, subjectLabel, row);

    const lesson = row.lesson || 'Unknown Chapter';
    const topic = row.topic || 'Unknown Topic';
    const key = `${subjectLabel} | ${lesson} | ${topic}`;
    addToGroup(lessonTopicGroups, key, `${lesson} | ${topic}`, row, {
      subject: subjectLabel,
      lesson,
      topic,
    });

    row.questions.forEach(question => {
      const questionType = question.questionType || 'Unknown Question Type';

      if (!questionTypeGroups[questionType]) {
        questionTypeGroups[questionType] = {
          label: questionType,
          count: 0,
          correct: 0,
          incorrect: 0,
        };
      }

      questionTypeGroups[questionType].count += 1;
      if (question.isCorrect || question.status === 'correct') {
        questionTypeGroups[questionType].correct += 1;
      } else {
        questionTypeGroups[questionType].incorrect += 1;
      }
    });
  });

  const questionTypeSummary = Object.values(questionTypeGroups)
    .map(item => ({
      ...item,
      avgPercent: item.count ? round1((item.correct / item.count) * 100) : 0,
    }))
    .sort((a, b) => a.avgPercent - b.avgPercent);

  return {
    totalWorksheetCount: scopedResults.length,
    totalQuestions: totals.total,
    correctCount: totals.correct,
    incorrectCount: Math.max(0, totals.total - totals.correct),
    averagePercent: totals.total ? round1((totals.correct / totals.total) * 100) : 0,
    subjectSummary: summarizeGroup(subjectGroups),
    lessonTopicSummary: summarizeGroup(lessonTopicGroups),
    questionTypeSummary,
    weakestQuestionType: questionTypeSummary[0] || null,
  };
};

const getPerformanceIntent = text =>
  /\b(performance|progress|score|scores|marks|report|reports|quiz results|worksheet results|result|results|average|averages|accuracy|weak|weakness|analysis)\b/i.test(text || '') &&
  /\b(quiz|quizzes|worksheet|worksheets|performance|progress|score|scores|marks|report|results|accuracy|weak|weakness|analysis)\b/i.test(text || '');

const getComparisonIntent = text =>
  /\b(compare|comparison|versus|vs|against|better|worse|rank|ranking)\b/i.test(text || '') &&
  /\b(performance|quiz|quizzes|score|scores|marks|subject|chapter|topic|class|school|schools|average)\b/i.test(text || '');

const getStrongComparisonIntent = text =>
  getComparisonIntent(text) ||
  /\b(top schools|across schools|between schools|class average|class maximum|class minimum|school average|school ranking|across subjects|across chapters|across topics)\b/i.test(text || '');

const getPossibleComparisonIntent = text =>
  getStrongComparisonIntent(text) ||
  /\b(compare|comparison|versus|vs|against|better|worse|rank|ranking|ahead|behind|stand|peers)\b/i.test(text || '');

const getSchoolComparisonIntent = text =>
  /\b(school|schools|school-wise|schoolwise|top schools|across schools)\b/i.test(text || '');

const normalizeComparisonScope = value => {
  const scope = normalizeText(value).replace(/\s+/g, '_');

  if (['school', 'schools', 'across_schools', 'between_schools', 'top_schools'].includes(scope)) return 'schools';
  if (['class', 'same_class', 'classmates', 'peer', 'peers'].includes(scope)) return 'class';
  if (['subject', 'subjects', 'across_subjects'].includes(scope)) return 'subjects';
  if (['chapter', 'chapters', 'lesson', 'lessons', 'across_chapters'].includes(scope)) return 'chapters';
  if (['topic', 'topics', 'across_topics'].includes(scope)) return 'topics';
  if (scope === 'none') return 'none';

  return 'general';
};

const getLocalComparisonDetails = text => {
  const comparisonPhrase = getPossibleComparisonIntent(text);
  const schoolScope =
    (comparisonPhrase && getSchoolComparisonIntent(text)) ||
    /\b(top schools|across schools|between schools|school average|school ranking|school-wise|schoolwise)\b/i.test(text || '');
  const classScope =
    (comparisonPhrase && /\b(class|classmates|peer|peers)\b/i.test(text || '')) ||
    /\b(class average|class maximum|class minimum)\b/i.test(text || '');
  const subjectScope =
    (comparisonPhrase && /\b(subject|subjects)\b/i.test(text || '')) ||
    /\bacross subjects\b/i.test(text || '');
  const chapterScope =
    (comparisonPhrase && /\b(chapter|chapters|lesson|lessons)\b/i.test(text || '')) ||
    /\bacross chapters\b/i.test(text || '');
  const topicScope =
    (comparisonPhrase && /\b(topic|topics)\b/i.test(text || '')) ||
    /\bacross topics\b/i.test(text || '');

  return {
    isComparison: comparisonPhrase || schoolScope || classScope || subjectScope || chapterScope || topicScope,
    scope: schoolScope
      ? 'schools'
      : classScope
        ? 'class'
        : subjectScope
          ? 'subjects'
          : chapterScope
            ? 'chapters'
            : topicScope
              ? 'topics'
              : 'general',
    subject: '',
  };
};

const parseIntentJson = value => {
  const rawText = String(value || '').trim();
  const match = rawText.replace(/^```json/i, '').replace(/^```/i, '').replace(/```$/i, '').match(/\{[\s\S]*\}/);

  if (!match) return null;

  try {
    return JSON.parse(match[0]);
  } catch (error) {
    console.error('Error parsing comparison intent:', error);
    return null;
  }
};

const sanitizeIntentQuestion = value =>
  String(value || '')
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
    .replace(/\b\d+(?:\.\d+)?(?:\s*\/\s*\d+(?:\.\d+)?)?%?\b/g, '[number]')
    .slice(0, 500);

const getRequestedSubject = (text, results) => {
  const question = normalizeSubjectText(text);
  const subjects = [...new Set(results.map(row => row.subject).filter(Boolean))]
    .sort((a, b) => b.length - a.length);

  return subjects.find(subject => {
    const normalizedSubject = normalizeSubjectText(subject);
    if (!normalizedSubject) return false;
    if (question.includes(normalizedSubject)) return true;

    return normalizedSubject
      .split(' ')
      .filter(part => part.length > 3)
      .some(part => question.includes(part));
  }) || '';
};

const summarizeGroup = groupedMap =>
  Object.values(groupedMap)
    .map(group => ({
      ...group,
      avgScore: group.count ? round1(group.totalScore / group.count) : 0,
      avgTotal: group.count ? round1(group.totalPossible / group.count) : 0,
      avgPercent: group.totalPossible ? round1((group.totalScore / group.totalPossible) * 100) : 0,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const addToGroup = (groupedMap, key, label, row, extra = {}) => {
  const score = formatScore(row);

  if (!groupedMap[key]) {
    groupedMap[key] = {
      key,
      label,
      count: 0,
      totalScore: 0,
      totalPossible: 0,
      ...extra,
    };
  }

  groupedMap[key].count += 1;
  groupedMap[key].totalScore += score.score;
  groupedMap[key].totalPossible += score.total;
};

const addToClassGroup = (groupedMap, key, label, row) => {
  const score = formatScore(row);

  if (!groupedMap[key]) {
    groupedMap[key] = {
      key,
      label,
      count: 0,
      totalScore: 0,
      totalPossible: 0,
      percents: [],
    };
  }

  groupedMap[key].count += 1;
  groupedMap[key].totalScore += score.score;
  groupedMap[key].totalPossible += score.total;
  groupedMap[key].percents.push(score.percent);
};

const summarizeClassGroup = groupedMap =>
  Object.values(groupedMap).reduce((acc, group) => {
    acc[group.key] = {
      classAvgPercent: group.totalPossible ? round1((group.totalScore / group.totalPossible) * 100) : 0,
      classMaxPercent: group.percents.length ? round1(Math.max(...group.percents)) : 0,
      classMinPercent: group.percents.length ? round1(Math.min(...group.percents)) : 0,
      classQuizCount: group.count,
    };
    return acc;
  }, {});

const withClassStats = (userSummary, classStats) =>
  userSummary.map(item => ({
    ...item,
    classAvgPercent: classStats[item.key]?.classAvgPercent ?? null,
    classMaxPercent: classStats[item.key]?.classMaxPercent ?? null,
    classMinPercent: classStats[item.key]?.classMinPercent ?? null,
    classQuizCount: classStats[item.key]?.classQuizCount ?? 0,
  }));

const getReportSubjectOptions = report => {
  const subjects = [
    report?.requestedSubject,
    ...(report?.subjectSummary || []).map(item => item.label),
    ...(report?.onlineWorksheetReport?.subjectSummary || []).map(item => item.label),
  ].filter(Boolean);

  return [...new Set(subjects)];
};

const getQuizPreviewQuestions = quizText =>
  String(quizText || '')
    .split('\n\n')
    .map(block => block.split('\n').find(line => line.toLowerCase().startsWith('question:')))
    .filter(Boolean)
    .map(line => line.replace(/question:/i, '').trim())
    .slice(0, 2);

function PerformanceReportCard({ report, onResourceRequest, resourceLoading }) {
  const subjectOptions = getReportSubjectOptions(report);
  const [selectedSubject, setSelectedSubject] = useState(subjectOptions[0] || '');
  const chosenSubject = selectedSubject || subjectOptions[0] || '';
  const worksheetReport = report?.onlineWorksheetReport || null;

  if (!report) return null;

  const subjectAvgData = {
    labels: report.subjectSummary.map(item => item.label),
    datasets: [
      {
        label: 'My average %',
        data: report.subjectSummary.map(item => item.avgPercent),
        backgroundColor: '#2563eb',
      },
      {
        label: 'Class average %',
        data: report.subjectSummary.map(item => item.classAvgPercent),
        backgroundColor: '#16a34a',
      },
      {
        label: 'Class maximum %',
        data: report.subjectSummary.map(item => item.classMaxPercent),
        backgroundColor: '#dc2626',
      },
      {
        label: 'Class minimum %',
        data: report.subjectSummary.map(item => item.classMinPercent),
        backgroundColor: '#f59e0b',
      },
    ],
  };

  const subjectCountData = {
    labels: report.subjectSummary.map(item => item.label),
    datasets: [
      {
        label: 'Quizzes taken',
        data: report.subjectSummary.map(item => item.count),
        backgroundColor: '#16a34a',
      },
    ],
  };

  const worksheetSubjectData = {
    labels: (worksheetReport?.subjectSummary || []).map(item => item.label),
    datasets: [
      {
        label: 'Online worksheet average %',
        data: (worksheetReport?.subjectSummary || []).map(item => item.avgPercent),
        backgroundColor: '#0891b2',
      },
    ],
  };

  const worksheetQuestionTypeData = {
    labels: (worksheetReport?.questionTypeSummary || []).map(item => item.label),
    datasets: [
      {
        label: 'Accuracy %',
        data: (worksheetReport?.questionTypeSummary || []).map(item => item.avgPercent),
        backgroundColor: '#ea580c',
      },
    ],
  };

  const lessonTopicData = {
    labels: report.lessonTopicSummary.map(item => item.label),
    datasets: [
      {
        label: 'My average %',
        data: report.lessonTopicSummary.map(item => item.avgPercent),
        backgroundColor: '#7c3aed',
      },
      {
        label: 'Class average %',
        data: report.lessonTopicSummary.map(item => item.classAvgPercent),
        backgroundColor: '#16a34a',
      },
      {
        label: 'Class maximum %',
        data: report.lessonTopicSummary.map(item => item.classMaxPercent),
        backgroundColor: '#dc2626',
      },
      {
        label: 'Class minimum %',
        data: report.lessonTopicSummary.map(item => item.classMinPercent),
        backgroundColor: '#f59e0b',
      },
    ],
  };

  const percentOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: value => `${value}%`,
        },
      },
    },
  };

  const countOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  };

  const lessonTopicOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: true },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: value => `${value}%`,
        },
      },
      y: {
        ticks: {
          autoSkip: false,
        },
      },
    },
  };

  return (
    <div className="mt-4 space-y-4 whitespace-normal">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <div className="rounded-xl bg-white border border-slate-200 p-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-black">Quizzes</p>
          <p className="text-2xl font-black text-slate-900">{report.totalQuizCount}</p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-black">Average</p>
          <p className="text-2xl font-black text-blue-600">{report.averagePercent}%</p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-black">Online Worksheets</p>
          <p className="text-2xl font-black text-cyan-700">{worksheetReport?.totalWorksheetCount || 0}</p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-black">Period</p>
          <p className="text-sm font-black text-slate-900">Last 1 year</p>
        </div>
      </div>

      {report.classOverall?.count > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
            <p className="text-[10px] uppercase tracking-wide text-emerald-600 font-black">Class Average</p>
            <p className="text-2xl font-black text-emerald-700">{report.classOverall.averagePercent}%</p>
          </div>
          <div className="rounded-xl bg-rose-50 border border-rose-100 p-3">
            <p className="text-[10px] uppercase tracking-wide text-rose-600 font-black">Class Maximum</p>
            <p className="text-2xl font-black text-rose-700">{report.classOverall.maxPercent}%</p>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
            <p className="text-[10px] uppercase tracking-wide text-amber-600 font-black">Class Minimum</p>
            <p className="text-2xl font-black text-amber-700">{report.classOverall.minPercent}%</p>
          </div>
        </div>
      )}

      {report.subjectSummary.length > 0 && (
        <div className="rounded-xl bg-white border border-slate-200 p-3">
          <p className="text-xs font-black text-slate-700 mb-3">Average quiz score by subject</p>
          <div className="h-64">
            <Bar data={subjectAvgData} options={percentOptions} />
          </div>
        </div>
      )}

      {report.subjectSummary.length > 0 && (
        <div className="rounded-xl bg-white border border-slate-200 p-3">
          <p className="text-xs font-black text-slate-700 mb-3">Number of quizzes taken by subject</p>
          <div className="h-64">
            <Bar data={subjectCountData} options={countOptions} />
          </div>
        </div>
      )}

      {report.lessonTopicSummary.length > 0 && (
        <div className="rounded-xl bg-white border border-slate-200 p-3">
          <p className="text-xs font-black text-slate-700 mb-3">
            Average score by chapter and topic {report.requestedSubject ? `for ${report.requestedSubject}` : ''}
          </p>
          <div className="h-96">
            <Bar data={lessonTopicData} options={lessonTopicOptions} />
          </div>
        </div>
      )}

      {worksheetReport?.totalWorksheetCount > 0 && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="rounded-xl bg-cyan-50 border border-cyan-100 p-3">
              <p className="text-[10px] uppercase tracking-wide text-cyan-700 font-black">Worksheet Average</p>
              <p className="text-2xl font-black text-cyan-800">{worksheetReport.averagePercent}%</p>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
              <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-black">Correct Questions</p>
              <p className="text-2xl font-black text-emerald-800">{worksheetReport.correctCount}</p>
            </div>
            <div className="rounded-xl bg-rose-50 border border-rose-100 p-3">
              <p className="text-[10px] uppercase tracking-wide text-rose-700 font-black">Incorrect Questions</p>
              <p className="text-2xl font-black text-rose-800">{worksheetReport.incorrectCount}</p>
            </div>
          </div>

          {worksheetReport.subjectSummary.length > 0 && (
            <div className="rounded-xl bg-white border border-slate-200 p-3">
              <p className="text-xs font-black text-slate-700 mb-3">Online worksheet average by subject</p>
              <div className="h-64">
                <Bar data={worksheetSubjectData} options={percentOptions} />
              </div>
            </div>
          )}

          {worksheetReport.questionTypeSummary.length > 0 && (
            <div className="rounded-xl bg-white border border-slate-200 p-3">
              <p className="text-xs font-black text-slate-700 mb-3">Online worksheet accuracy by question type</p>
              <div className="h-80">
                <Bar data={worksheetQuestionTypeData} options={lessonTopicOptions} />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl bg-blue-50 border border-blue-100 p-3">
        <p className="text-sm font-black text-slate-800">
          Choose a subject and I will suggest existing worksheets and quizzes from Eascode.
        </p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <select
            value={chosenSubject}
            onChange={event => setSelectedSubject(event.target.value)}
            className="min-h-10 flex-1 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-bold text-slate-800 outline-none focus:border-blue-500"
          >
            {subjectOptions.map(subject => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => onResourceRequest(chosenSubject)}
            disabled={!chosenSubject || resourceLoading}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-black text-white transition hover:bg-blue-700 disabled:opacity-50"
          >
            {resourceLoading && <Loader2 className="animate-spin" size={14} />}
            Show suggestions
          </button>
        </div>
      </div>
    </div>
  );
}

function CrossSchoolComparisonCard({ report }) {
  if (!report || !report.topSchools?.length) return null;

  const comparisonData = {
    labels: ['My average', ...report.topSchools.map(item => item.school)],
    datasets: [
      {
        label: 'Average score %',
        data: [report.studentAveragePercent, ...report.topSchools.map(item => item.avgPercent)],
        backgroundColor: ['#2563eb', '#16a34a', '#dc2626', '#f59e0b'],
      },
    ],
  };

  const comparisonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: value => `${value}%`,
        },
      },
      y: {
        ticks: {
          autoSkip: false,
        },
      },
    },
  };

  return (
    <div className="mt-4 space-y-3 whitespace-normal">
      <div className="rounded-xl bg-white border border-slate-200 p-3">
        <p className="text-xs font-black text-slate-700 mb-1">
          Across-school comparison for {report.subject}
        </p>
        <p className="text-[11px] font-semibold text-slate-500 mb-3">
          Same standard: {report.standard || 'not provided'} | Last 1 year
        </p>
        <div className="h-72">
          <Bar data={comparisonData} options={comparisonOptions} />
        </div>
      </div>
    </div>
  );
}

function ResourceSuggestionCard({ suggestions, onWorksheetDownload, onQuizOpen }) {
  if (!suggestions) return null;

  return (
    <div className="mt-4 space-y-4 whitespace-normal">
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-center gap-2">
          <BookOpen size={16} className="text-blue-600" />
          <p className="text-xs font-black uppercase tracking-wide text-slate-400">Selected Subject</p>
        </div>
        <p className="mt-1 text-lg font-black text-slate-900">{suggestions.subject}</p>
        <p className="text-[11px] font-semibold text-slate-500">
          Standard {suggestions.standard || 'not provided'} | Ranked by usage and latest upload
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-black text-slate-800">
          <FilePlus size={16} className="text-blue-600" />
          Worksheets
        </div>
        {suggestions.worksheets.length > 0 ? (
          suggestions.worksheets.map(worksheet => (
            <div key={worksheet.id} className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-sm font-black text-slate-900">{worksheet.title}</p>
              <p className="mt-1 text-xs font-semibold text-slate-500">
                {worksheet.lessonName} | {worksheet.topics}
              </p>
              <p className="mt-1 text-[11px] font-semibold text-slate-400">
                Downloads: {worksheet.downloads} | Uploaded: {worksheet.uploadedAt}
              </p>
              <button
                type="button"
                onClick={() => onWorksheetDownload(worksheet)}
                className="mt-3 inline-flex items-center justify-center rounded-lg bg-blue-600 px-3 py-2 text-xs font-black text-white transition hover:bg-blue-700"
              >
                Download
              </button>
            </div>
          ))
        ) : (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-3 text-xs font-semibold text-slate-500">
            No worksheet found for this subject yet.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-black text-slate-800">
          <Brain size={16} className="text-indigo-600" />
          Quizzes
        </div>
        {suggestions.quizzes.length > 0 ? (
          suggestions.quizzes.map(quiz => (
            <div key={quiz.id} className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-sm font-black text-slate-900">{quiz.title}</p>
              <p className="mt-1 text-xs font-semibold text-slate-500">
                {quiz.lessonName} | {quiz.topic}
              </p>
              <p className="mt-1 text-[11px] font-semibold text-slate-400">
                Retakes: {quiz.retakeCount} | Uploaded: {quiz.uploadedAt}
              </p>
              {getQuizPreviewQuestions(quiz.quizText).length > 0 && (
                <div className="mt-3 space-y-2">
                  {getQuizPreviewQuestions(quiz.quizText).map((question, index) => (
                    <p key={`${quiz.id}-${index}`} className="rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                      {index + 1}. {question}
                    </p>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => onQuizOpen(quiz)}
                className="mt-3 inline-flex items-center justify-center rounded-lg bg-indigo-600 px-3 py-2 text-xs font-black text-white transition hover:bg-indigo-700"
              >
                Take Quiz
              </button>
            </div>
          ))
        ) : (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-3 text-xs font-semibold text-slate-500">
            No quiz found for this subject yet.
          </p>
        )}
      </div>
    </div>
  );
}

function EascodeChat() {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const redirectTimersRef = useRef([]);

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [messages, setMessages] = useState(loadSessionMessages);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [resourceLoading, setResourceLoading] = useState(false);

  const [chatSessionId, setChatSessionId] = useState(
  () => sessionStorage.getItem(CHAT_FIRESTORE_SESSION_KEY) || ''
);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      setUser(currentUser);
      setCheckingAuth(false);

      if (!currentUser) {
        setProfile(null);
        return;
      }

      try {
        const profileSnap = await getDoc(doc(db, 'profiles', currentUser.uid));
        setProfile(profileSnap.exists() ? profileSnap.data() : null);
      } catch (error) {
        console.error('Error loading chat profile:', error);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  useEffect(() => {
    return () => {
      redirectTimersRef.current.forEach(timerId => clearInterval(timerId));
      redirectTimersRef.current = [];
    };
  }, []);

  useEffect(() => {
    try {
      const lastMessages = messages.slice(-8).map(getSerializableMessage);
      sessionStorage.setItem(CHAT_SESSION_KEY, JSON.stringify(lastMessages));
    } catch (error) {
      console.error('Error saving Eascode chat session:', error);
    }
  }, [messages]);

  const handleLogin = () => {
    sessionStorage.setItem('eascode_post_login_redirect', '/EascodeChat');
    sessionStorage.removeItem('eascode_auth_redirect_started');
    sessionStorage.removeItem('eascode_auth_redirect_retry');
    navigate('/auth-loading');
  };

  const buildPrompt = (chatMessages, question) => {
    const history = chatMessages
      .slice(-8)
      .map(message => `${message.role === 'assistant' ? 'Eascode' : 'User'}: ${message.text}`)
      .join('\n');

    return `
You are Eascode, a warm education assistant for parents, teachers, and students.
You are only an information sharing and routing interface.

Strict scope:
- Answer only questions about Eascode tool usage, quiz, online worksheet, and marks performance analysis, and which Eascode utility the user should open.
- Do not create worksheets, quizzes, test questions, practice exercises, answer keys, chapter explanations, chapter summaries, or chapter-content answers in this chat.
- If the user asks to create a worksheet, route them to the worksheet utility: /Eascode_Agent_Lesson.
- If the user asks to create or take a quiz, route them to the quiz utility: /AIQuestionTest.
- If the user asks to learn, study, explore, discuss, explain, summarize, solve, or ask questions about chapter/topic/concept content, route them to the chapter discussion utility: /ChapterChat.
- If a request is outside Eascode tool usage, marks/performance analysis, or utility routing, politely say that Easy can help only with Eascode usage, performance analysis, and opening the right study utility.
- Always be courteous, child-safe, and professional. Never use foul, insulting, sexual, violent, or abusive language.

Student profile:
- Standard: ${profile?.standard || 'not provided'}
- School: ${profile?.school || 'not provided'}
- City: ${profile?.city || 'not provided'}

Chat so far:
${history}

Latest user request:
${question}

Reply clearly and practically, but stay inside the strict scope above.
`.trim();
  };

  const getGuestReply = question => (
    `I can help with Eascode tool usage, quiz and online worksheet performance analysis after sign-in, and opening the right study utility.\n\nFor "${question}", please sign in so I can route you to the correct Eascode tool.`
  );

  const fetchGeminiText = async (prompt, generationConfig = {}) => {
    const callGemini = httpsCallable(functions, 'callGemini');
    const result = await callGemini({
      contents: [
        {
          role: 'user',
          parts: [{ text: prompt }],
        },
      ],
      generationConfig: {
        maxOutputTokens: generationConfig.maxOutputTokens || 20480,
        temperature: generationConfig.temperature ?? 0.1,
      },
    });

    return (
      result.data?.candidates?.[0]?.content?.parts?.[0]?.text ||
      'I could not generate a response right now. Please try again.'
    );
  };

  const fetchAIResponse = async (chatMessages, question) => {
    return fetchGeminiText(buildPrompt(chatMessages, question));
  };

  const fetchComparisonIntent = async question => {
    const localIntent = getLocalComparisonDetails(question);
    const sanitizedQuestion = sanitizeIntentQuestion(question);

    try {
      const intentText = await fetchGeminiText(
        `
You are only an intent parser for an education app.
Return only valid JSON. Do not answer the user.
Use only the user request below. Do not ask for or infer quiz marks, averages, school rankings, or Firestore data.

JSON shape:
{
  "isComparison": true,
  "scope": "schools | class | subjects | chapters | topics | general | none",
  "subject": "subject mentioned by the user, or empty string"
}

User request:
${sanitizedQuestion}
`.trim(),
        { maxOutputTokens: 256, temperature: 0 }
      );
      const parsedIntent = parseIntentJson(intentText);

      if (!parsedIntent) return localIntent;

      const parsedComparison = parsedIntent.isComparison === true || normalizeText(parsedIntent.isComparison) === 'true';
      const parsedScope = normalizeComparisonScope(parsedIntent.scope);
      const localScope = normalizeComparisonScope(localIntent.scope);
      const strongLocalIntent = getStrongComparisonIntent(question);
      const scope = parsedScope && parsedScope !== 'none' && parsedScope !== 'general'
        ? parsedScope
        : localScope;

      return {
        isComparison: parsedComparison || strongLocalIntent,
        scope,
        subject: typeof parsedIntent.subject === 'string' ? parsedIntent.subject.trim() : '',
      };
    } catch (error) {
      console.error('Error reading comparison intent:', error);
      return localIntent;
    }
  };

  const fetchQuizPerformanceReport = async question => {
    if (!user) {
      return {
        text: 'Please sign in first so I can read your quiz and online worksheet performance securely.',
        report: null,
      };
    }

    const userUid = profile?.uid || user.uid;
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    const [resultsSnap, onlineWorksheetSnap] = await Promise.all([
      getDocs(
        query(
          collection(db, 'AI_Results'),
          where('uid', '==', userUid)
        )
      ),
      getDocs(
        query(
          collection(db, 'Online_Worksheet_Results'),
          where('user_uid', '==', userUid)
        )
      ),
    ]);

    const allResults = resultsSnap.docs
      .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
      .filter(row => {
        const resultDate = toDate(row.timestamp);
        return resultDate && resultDate >= oneYearAgo;
      });
    const onlineWorksheetResults = onlineWorksheetSnap.docs
      .map(docSnap => normalizeOnlineWorksheetResult({ id: docSnap.id, ...docSnap.data() }))
      .filter(row => {
        const resultDate = toDate(row.timestamp);
        return resultDate && resultDate >= oneYearAgo;
      });

    if (allResults.length === 0 && onlineWorksheetResults.length === 0) {
      return {
        text: 'I could not find any quiz or online worksheet attempts from the last 1 year. Take a quiz or solve a worksheet online first and I will build your performance report here.',
        report: null,
      };
    }

    const requestedSubject = getRequestedSubject(question, [...allResults, ...onlineWorksheetResults]);
    const scopedResults = requestedSubject
      ? allResults.filter(row => normalizeSubjectText(row.subject) === normalizeSubjectText(requestedSubject))
      : allResults;
    const onlineWorksheetReport = buildOnlineWorksheetPerformance(onlineWorksheetResults, requestedSubject);

    if (requestedSubject && scopedResults.length === 0 && !onlineWorksheetReport) {
      const availableSubjects = [...new Set(
        [...allResults, ...onlineWorksheetResults].map(row => row.subject).filter(Boolean)
      )].sort();

      return {
        text: `I found performance data from the last 1 year, but not for ${requestedSubject}. Available subjects are: ${availableSubjects.join(', ')}.`,
        report: null,
      };
    }

    const classSchool = profile?.school || scopedResults[0]?.school || onlineWorksheetResults[0]?.school || allResults[0]?.school || '';
    const classStandard = profile?.standard || scopedResults[0]?.standard || onlineWorksheetResults[0]?.standard || allResults[0]?.standard || '';
    let classResults = [];

    if (scopedResults.length > 0 && classSchool && classStandard) {
      try {
        const classResultsSnap = await getDocs(
          query(
            collection(db, 'AI_Results'),
            where('school', '==', classSchool),
            where('standard', '==', classStandard)
          )
        );

        classResults = classResultsSnap.docs
          .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
          .filter(row => {
            const resultDate = toDate(row.timestamp);
            if (!resultDate || resultDate < oneYearAgo) return false;
            if (!requestedSubject) return true;
            return normalizeSubjectText(row.subject) === normalizeSubjectText(requestedSubject);
          });
      } catch (error) {
        console.error('Error loading class performance:', error);
      }
    }

    const subjectGroups = {};
    scopedResults.forEach(row => {
      const subjectLabel = row.subject || 'Unknown Subject';
      addToGroup(subjectGroups, subjectLabel, subjectLabel, row);
    });

    const classSubjectGroups = {};
    classResults.forEach(row => {
      const subjectLabel = row.subject || 'Unknown Subject';
      addToClassGroup(classSubjectGroups, subjectLabel, subjectLabel, row);
    });

    const lessonTopicGroups = {};
    if (requestedSubject) {
      scopedResults.forEach(row => {
        const lesson = row.lesson || 'Unknown Chapter';
        const topic = row.topic || 'Unknown Topic';
        const key = `${requestedSubject} | ${lesson} | ${topic}`;

        addToGroup(lessonTopicGroups, key, `${lesson} | ${topic}`, row, {
          subject: requestedSubject,
          lesson,
          topic,
        });
      });
    }

    const classLessonTopicGroups = {};
    if (requestedSubject) {
      classResults.forEach(row => {
        const lesson = row.lesson || 'Unknown Chapter';
        const topic = row.topic || 'Unknown Topic';
        const key = `${requestedSubject} | ${lesson} | ${topic}`;

        addToClassGroup(classLessonTopicGroups, key, `${lesson} | ${topic}`, row);
      });
    }

    const totals = scopedResults.reduce(
      (acc, row) => {
        const score = formatScore(row);
        acc.score += score.score;
        acc.total += score.total;
        return acc;
      },
      { score: 0, total: 0 }
    );

    const classPercents = classResults.map(row => formatScore(row).percent);
    const classTotals = classResults.reduce(
      (acc, row) => {
        const score = formatScore(row);
        acc.score += score.score;
        acc.total += score.total;
        return acc;
      },
      { score: 0, total: 0 }
    );
    const classOverall = {
      count: classResults.length,
      averagePercent: classTotals.total ? round1((classTotals.score / classTotals.total) * 100) : null,
      maxPercent: classPercents.length ? round1(Math.max(...classPercents)) : null,
      minPercent: classPercents.length ? round1(Math.min(...classPercents)) : null,
      school: classSchool,
      standard: classStandard,
    };

    const subjectSummary = withClassStats(summarizeGroup(subjectGroups), summarizeClassGroup(classSubjectGroups));
    const lessonTopicSummary = withClassStats(summarizeGroup(lessonTopicGroups), summarizeClassGroup(classLessonTopicGroups));
    const weakestLessonTopic = lessonTopicSummary
      .filter(item => item.count > 0)
      .sort((a, b) => a.avgPercent - b.avgPercent)[0];
    const weakestSubject = subjectSummary
      .filter(item => item.count > 0)
      .sort((a, b) => a.avgPercent - b.avgPercent)[0];

    const recommendedPractice = weakestLessonTopic
      ? {
          subject: requestedSubject || weakestLessonTopic.subject,
          lesson: weakestLessonTopic.lesson || '',
          topic: weakestLessonTopic.topic || '',
          label: weakestLessonTopic.label,
          avgPercent: weakestLessonTopic.avgPercent,
        }
      : weakestSubject
        ? {
            subject: weakestSubject.label,
            lesson: '',
            topic: '',
            label: weakestSubject.label,
            avgPercent: weakestSubject.avgPercent,
          }
        : null;

    const report = {
      requestedSubject,
      totalQuizCount: scopedResults.length,
      averagePercent: totals.total ? round1((totals.score / totals.total) * 100) : 0,
      subjectSummary,
      lessonTopicSummary,
      recommendedPractice,
      classOverall,
      onlineWorksheetReport,
    };

    const practiceQuestion = ' Choose a subject below and I will suggest existing Eascode worksheets and quizzes for it.';
    const classComparisonText = classOverall.count > 0
      ? ` I also compared this with ${classOverall.count} class quiz attempt${classOverall.count === 1 ? '' : 's'} from the same school and standard.`
      : '';
    const worksheetText = onlineWorksheetReport?.totalWorksheetCount > 0
      ? ` You also completed ${onlineWorksheetReport.totalWorksheetCount} online worksheet${onlineWorksheetReport.totalWorksheetCount === 1 ? '' : 's'}, with an average score of ${onlineWorksheetReport.averagePercent}%. I added worksheet accuracy by subject and question type below.`
      : '';
    const text = scopedResults.length > 0
      ? requestedSubject
        ? `Here is your ${requestedSubject} performance for the last 1 year. You have taken ${report.totalQuizCount} quiz${report.totalQuizCount === 1 ? '' : 'zes'}, with an average quiz score of ${report.averagePercent}%. I also added chapter and topic averages below.${worksheetText}${classComparisonText}${practiceQuestion}`
        : `Here is your performance for the last 1 year. You have taken ${report.totalQuizCount} quiz${report.totalQuizCount === 1 ? '' : 'zes'}, with an average quiz score of ${report.averagePercent}%. I added average quiz score by subject and quiz count by subject below.${worksheetText}${classComparisonText}${practiceQuestion}`
      : requestedSubject
        ? `Here is your ${requestedSubject} online worksheet performance for the last 1 year. You completed ${onlineWorksheetReport.totalWorksheetCount} online worksheet${onlineWorksheetReport.totalWorksheetCount === 1 ? '' : 's'}, with an average score of ${onlineWorksheetReport.averagePercent}%. I added worksheet accuracy by subject and question type below.${practiceQuestion}`
        : `Here is your online worksheet performance for the last 1 year. You completed ${onlineWorksheetReport.totalWorksheetCount} online worksheet${onlineWorksheetReport.totalWorksheetCount === 1 ? '' : 's'}, with an average score of ${onlineWorksheetReport.averagePercent}%. I added worksheet accuracy by subject and question type below.${practiceQuestion}`;

    return { text, report };
  };

  const fetchLocalComparisonResponse = async (question, comparisonIntent = {}) => {
    if (!user) {
      return {
        text: 'Please sign in first so I can compare your performance securely.',
        performanceReport: null,
        comparisonReport: null,
      };
    }

    const comparisonScope = normalizeComparisonScope(comparisonIntent.scope);

    if (comparisonScope !== 'schools' && !getSchoolComparisonIntent(question)) {
      const performanceResponse = await fetchQuizPerformanceReport(question);
      return {
        text: performanceResponse.text,
        performanceReport: performanceResponse.report,
        comparisonReport: null,
      };
    }

    const userUid = profile?.uid || user.uid;
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    const userResultsSnap = await getDocs(
      query(
        collection(db, 'AI_Results'),
        where('uid', '==', userUid)
      )
    );

    const userResults = userResultsSnap.docs
      .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
      .filter(row => {
        const resultDate = toDate(row.timestamp);
        return resultDate && resultDate >= oneYearAgo;
      });

    if (userResults.length === 0) {
      return {
        text: 'I could not find your quiz attempts from the last 1 year, so I do not have enough data for an across-school comparison yet.',
        performanceReport: null,
        comparisonReport: null,
      };
    }

    const requestedSubject =
      getRequestedSubject(question, userResults) ||
      getRequestedSubject(comparisonIntent.subject, userResults);
    if (!requestedSubject) {
      const availableSubjects = [...new Set(userResults.map(row => row.subject).filter(Boolean))].sort();

      return {
        text: `Please mention the subject for across-school comparison. I found your quiz data for: ${availableSubjects.join(', ')}.`,
        performanceReport: null,
        comparisonReport: null,
      };
    }

    const scopedUserResults = userResults.filter(
      row => normalizeSubjectText(row.subject) === normalizeSubjectText(requestedSubject)
    );

    if (scopedUserResults.length === 0) {
      return {
        text: `I could not find your quiz attempts for ${requestedSubject} in the last 1 year.`,
        performanceReport: null,
        comparisonReport: null,
      };
    }

    const classStandard = profile?.standard || scopedUserResults[0]?.standard || userResults[0]?.standard || '';
    if (!classStandard) {
      return {
        text: 'I found your subject data, but I could not identify your standard for a fair across-school comparison.',
        performanceReport: null,
        comparisonReport: null,
      };
    }

    const userTotals = scopedUserResults.reduce(
      (acc, row) => {
        const score = formatScore(row);
        acc.score += score.score;
        acc.total += score.total;
        return acc;
      },
      { score: 0, total: 0 }
    );

    const schoolResultsSnap = await getDocs(
      query(
        collection(db, 'AI_Results'),
        where('standard', '==', classStandard),
        where('subject', '==', requestedSubject)
      )
    );

    const schoolGroups = {};
    schoolResultsSnap.docs
      .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
      .filter(row => {
        const resultDate = toDate(row.timestamp);
        return resultDate && resultDate >= oneYearAgo && row.school;
      })
      .forEach(row => {
        addToClassGroup(schoolGroups, row.school, row.school, row);
      });

    const topSchools = Object.values(schoolGroups)
      .map(group => ({
        school: group.label,
        avgPercent: group.totalPossible ? round1((group.totalScore / group.totalPossible) * 100) : 0,
        quizCount: group.count,
      }))
      .sort((a, b) => b.avgPercent - a.avgPercent)
      .slice(0, 3);

    if (topSchools.length === 0) {
      return {
        text: `I found your ${requestedSubject} performance, but I could not find school-level comparison data for the same standard in the last 1 year.`,
        performanceReport: null,
        comparisonReport: null,
      };
    }

    const comparisonReport = {
      subject: requestedSubject,
      standard: classStandard,
      studentAveragePercent: userTotals.total ? round1((userTotals.score / userTotals.total) * 100) : 0,
      studentQuizCount: scopedUserResults.length,
      topSchools,
    };

    const topSchoolText = topSchools
      .map((school, index) => `${index + 1}. ${school.school}: ${school.avgPercent}% (${school.quizCount} quiz attempts)`)
      .join('\n');

    return {
      text: `Here is your across-school comparison for ${requestedSubject}, Standard ${classStandard}, over the last 1 year.\n\nYour average: ${comparisonReport.studentAveragePercent}% across ${comparisonReport.studentQuizCount} quiz${comparisonReport.studentQuizCount === 1 ? '' : 'zes'}.\n\nTop 3 school averages:\n${topSchoolText}\n\nA good next step is to open the right Eascode practice utility for ${requestedSubject}.`,
      performanceReport: null,
      comparisonReport,
    };
  };

  const fetchResourceSuggestions = async subject => {
    if (!user) {
      throw new Error('Sign in required for resource suggestions.');
    }

    const selectedSubject = subject || '';
    const normalizedSubject = normalizeSubjectText(selectedSubject);
    const standard = profile?.standard ? String(profile.standard) : '';

    const getDocsBySubject = async collectionName => {
      const constraints = [where('subject', '==', selectedSubject)];
      if (standard) constraints.push(where('standard', '==', standard));

      const exactSnap = await getDocs(query(collection(db, collectionName), ...constraints));
      if (exactSnap.docs.length > 0) return exactSnap.docs;

      const fallbackConstraints = standard ? [where('standard', '==', standard)] : [];
      const fallbackSnap = await getDocs(query(collection(db, collectionName), ...fallbackConstraints));
      return fallbackSnap.docs.filter(docSnap =>
        normalizeSubjectText(docSnap.data().subject) === normalizedSubject
      );
    };

    const [uploadDocs, quizDocs] = await Promise.all([
      getDocsBySubject('uploads'),
      getDocsBySubject('AI_Results'),
    ]);

    const worksheets = uploadDocs
      .map(docSnap => {
        const data = docSnap.data();
        const type = data.type || 'Worksheet';

        return {
          id: docSnap.id,
          title: data.calculatedFileName || data.fileName || `${selectedSubject} Worksheet`,
          fileUrl: data.fileUrl || '',
          subject: data.subject || selectedSubject,
          standard: data.standard || standard,
          type,
          uid: data.uid || '',
          uploadedBy: data.uploadedBy || 'User Not Found',
          creatorName: data.uploadername || data.uploadedBy || 'User Not Found',
          downloads: toNumber(data.downloads),
          uploadTimeMs: getTimeValue(data.uploadTime),
          uploadedAt: formatDateLabel(data.uploadTime),
          lessonName: data.lessonName || 'Not Available',
          topics: formatTopicValue(data.topicName),
        };
      })
      .filter(item => item.fileUrl && normalizeText(item.type).includes('worksheet'))
      .sort((a, b) => (b.downloads - a.downloads) || (b.uploadTimeMs - a.uploadTimeMs))
      .slice(0, 3);

    const quizzes = quizDocs
      .map(docSnap => {
        const data = docSnap.data();

        return {
          id: docSnap.id,
          title: `${data.subject || selectedSubject} Quiz - ${data.topic || 'Topic'}`,
          quizText: data.Quiz_Text || '',
          subject: data.subject || selectedSubject,
          standard: data.standard || standard,
          school: data.school || '',
          lessonName: data.lesson || 'Not Available',
          topic: data.topic || 'Not Available',
          format: data.format || 'MCQ',
          retakeCount: toNumber(data.retake_count),
          uploadTimeMs: getTimeValue(data.timestamp),
          uploadedAt: formatDateLabel(data.timestamp),
          totalQuestions: toNumber(data.totalQuestions),
          originalQuizId: docSnap.id,
        };
      })
      .filter(item => item.quizText && item.quizText !== 'No_Content')
      .sort((a, b) => (b.retakeCount - a.retakeCount) || (b.uploadTimeMs - a.uploadTimeMs))
      .slice(0, 3);

    return {
      subject: selectedSubject,
      standard,
      worksheets,
      quizzes,
    };
  };

  const getOrCreateChatSession = async () => {
  if (!user) return '';

  if (chatSessionId) {
    return chatSessionId;
  }

  const sessionRef = await addDoc(collection(db, 'EascodeChatSessions'), {
    uid: user.uid,
    email: user.email || '',
    standard: profile?.standard || '',
    school: profile?.school || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    source: 'EascodeChat',
  });

  sessionStorage.setItem(CHAT_FIRESTORE_SESSION_KEY, sessionRef.id);
  setChatSessionId(sessionRef.id);

  return sessionRef.id;
};

const saveChatTurnToFirestore = async ({ question, answer, performanceReport, comparisonReport }) => {
  if (!user) return;

  const sessionId = await getOrCreateChatSession();
  if (!sessionId) return;

  const sessionRef = doc(db, 'EascodeChatSessions', sessionId);

  await addDoc(collection(sessionRef, 'messages'), {
    uid: user.uid,
    email: user.email || '',
    question,
    answer,
    performanceReport: performanceReport || null,
    comparisonReport: comparisonReport || null,
    createdAt: serverTimestamp(),
  });

  await updateDoc(sessionRef, {
    updatedAt: serverTimestamp(),
    lastQuestion: question,
    lastAnswer: answer,
  });
};

  const handleSend = async (overrideText = '', guestAnswer = '') => {
    const text = (overrideText || input).trim();
    if (!text || sending) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      text,
    };
    const nextMessages = [...messages, userMessage];

    setMessages(nextMessages);
    setInput('');
    setSending(true);

    try {
      let responseText = '';
      let performanceReport = null;
      let comparisonReport = null;
      let redirectIntent = null;
      const toolRedirectIntent = getToolRedirectIntent(text);
      const performanceIntent = getPerformanceIntent(text);
      const possibleComparisonIntent = getPossibleComparisonIntent(text);

      if (performanceIntent) {
        trackClick('EascodeChat Performance Asked');
      }

      if (guestAnswer) {
        responseText = guestAnswer;
      } else if (user && possibleComparisonIntent) {
        const comparisonIntent = await fetchComparisonIntent(text);

        if (comparisonIntent.isComparison) {
          const comparisonResponse = await fetchLocalComparisonResponse(text, comparisonIntent);
          responseText = comparisonResponse.text;
          performanceReport = comparisonResponse.performanceReport;
          comparisonReport = comparisonResponse.comparisonReport;
        } else if (performanceIntent) {
          const performanceResponse = await fetchQuizPerformanceReport(text);
          responseText = performanceResponse.text;
          performanceReport = performanceResponse.report;
        } else if (toolRedirectIntent) {
          redirectIntent = toolRedirectIntent;
          responseText = getRedirectMessage(toolRedirectIntent);
        } else {
          responseText = await fetchAIResponse(nextMessages, text);
        }
      } else if (user && performanceIntent) {
        const performanceResponse = await fetchQuizPerformanceReport(text);
        responseText = performanceResponse.text;
        performanceReport = performanceResponse.report;
      } else if (toolRedirectIntent) {
        redirectIntent = toolRedirectIntent;
        responseText = getRedirectMessage(toolRedirectIntent);
      } else {
        responseText = user
          ? await fetchAIResponse(nextMessages, text)
          : guestAnswer || getGuestReply(text);
      }

      const assistantMessageId = `assistant-${Date.now()}`;

      setMessages(prev => [
        ...prev,
        {
          id: assistantMessageId,
          role: 'assistant',
          text: responseText,
          performanceReport,
          comparisonReport,
        },
      ]);

      await saveChatTurnToFirestore({
        question: text,
        answer: responseText,
        performanceReport,
        comparisonReport,
      });

      if (redirectIntent) {
        startRedirectCountdown(redirectIntent, assistantMessageId);
      }
    } catch (error) {
      console.error('Eascode chat error:', error);
      setMessages(prev => [
        ...prev,
        {
          id: `assistant-error-${Date.now()}`,
          role: 'assistant',
          text: 'The AI service could not respond. Please refresh and try again.',
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const openTool = path => {
    if (!user) {
      handleLogin();
      return;
    }

    navigate(path);
  };

  const startRedirectCountdown = (redirectIntent, messageId) => {
    let secondsLeft = REDIRECT_DELAY_SECONDS;

    const intervalId = setInterval(() => {
      secondsLeft -= 1;

      setMessages(prev =>
        prev.map(message =>
          message.id === messageId
            ? { ...message, text: getRedirectMessage(redirectIntent, secondsLeft) }
            : message
        )
      );

      if (secondsLeft <= 0) {
        clearInterval(intervalId);
        redirectTimersRef.current = redirectTimersRef.current.filter(timerId => timerId !== intervalId);

        const timeoutId = setTimeout(() => {
          openTool(redirectIntent.path);
          redirectTimersRef.current = redirectTimersRef.current.filter(timerId => timerId !== timeoutId);
        }, 250);

        redirectTimersRef.current.push(timeoutId);
      }
    }, 1000);

    redirectTimersRef.current.push(intervalId);
  };

  const handleResourceRequest = async subject => {
    if (!subject || resourceLoading) return;

    setResourceLoading(true);
    setSending(true);

    try {
      const suggestions = await fetchResourceSuggestions(subject);
      const hasSuggestions = suggestions.worksheets.length > 0 || suggestions.quizzes.length > 0;

      setMessages(prev => [
        ...prev,
        {
          id: `resources-${Date.now()}`,
          role: 'assistant',
          text: hasSuggestions
            ? `Here are existing Eascode resources for ${subject}. I found up to 3 worksheets and 3 quizzes ranked by usage and latest upload.`
            : `I checked Eascode resources for ${subject}, but I could not find worksheets or quizzes for this subject yet.`,
          resourceSuggestions: suggestions,
        },
      ]);
    } catch (error) {
      console.error('Error fetching Eascode resources:', error);
      setMessages(prev => [
        ...prev,
        {
          id: `resources-error-${Date.now()}`,
          role: 'assistant',
          text: 'I could not fetch worksheet and quiz suggestions right now. Please try again.',
        },
      ]);
    } finally {
      setSending(false);
      setResourceLoading(false);
    }
  };

  const handleWorksheetDownload = async worksheet => {
    if (!worksheet?.fileUrl) return;

    window.open(worksheet.fileUrl, '_blank', 'noopener,noreferrer');

    try {
      if (worksheet.id) {
        await updateDoc(doc(db, 'uploads', worksheet.id), {
          downloads: increment(1),
        });
      }

      if (user) {
        await addDoc(collection(db, 'Downloads_And_Generations'), {
          userId: user.uid,
          email: user.email,
          action: 'Download',
          fileName: worksheet.title,
          fileUrl: worksheet.fileUrl,
          subject: worksheet.subject,
          type: worksheet.type,
          standard: worksheet.standard,
          device: getDeviceType(),
          timestamp: new Date(),
        });
      }

      setMessages(prev =>
        prev.map(message => {
          if (!message.resourceSuggestions?.worksheets) return message;

          return {
            ...message,
            resourceSuggestions: {
              ...message.resourceSuggestions,
              worksheets: message.resourceSuggestions.worksheets.map(item =>
                item.id === worksheet.id
                  ? { ...item, downloads: toNumber(item.downloads) + 1 }
                  : item
              ),
            },
          };
        })
      );
    } catch (error) {
      console.error('Error logging worksheet download:', error);
    }
  };

  const handleQuizOpen = quiz => {
    navigate('/SameQuiz', {
      state: {
        Quiz_Text: quiz.quizText,
        subject: quiz.subject,
        topic: quiz.topic,
        format: quiz.format,
        standard: quiz.standard,
        lesson: quiz.lessonName,
        school: quiz.school,
        originalQuizId: quiz.originalQuizId,
      },
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 font-[Quicksand] text-slate-900">
      <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-3 py-3 shadow-sm sm:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="h-10 w-10 shrink-0 rounded-full border border-slate-200 bg-white text-slate-600 flex items-center justify-center hover:border-blue-300 hover:text-blue-600 transition"
            title="Back"
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <img
            src="./Eascode_No_Background.png"
            alt="Eascode"
            className="h-12 w-12 rounded-full object-contain"
          />
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-black leading-tight truncate">Eascode Chat</h1>
            <p className="text-[11px] sm:text-xs text-slate-500 font-semibold truncate">
              {user ? profile?.name || user.displayName || user.email : 'Guest mode'}
            </p>
          </div>
        </div>

        {checkingAuth ? (
          <Loader2 className="animate-spin text-blue-600" size={22} />
        ) : user ? (
          <button
            type="button"
            onClick={() => navigate('/Old_Home')}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-xs sm:text-sm font-bold text-white hover:bg-blue-700 transition"
          >
          <span className="text-[10px] sm:text-sm">🏠</span> 
          <span className="leading-none">Home</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={handleLogin}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-xs sm:text-sm font-bold text-white hover:bg-blue-700 transition"
          >
            <LogIn size={16} />
            Sign in
          </button>
        )}
      </header>

      <main className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-[300px_1fr]">
        <aside className="border-b border-slate-200 bg-white p-3 sm:p-4 lg:min-h-[calc(100vh-73px)] lg:border-b-0 lg:border-r">
          <div className="grid max-h-44 grid-cols-2 gap-2 overflow-y-auto pr-1 sm:max-h-52 lg:max-h-[calc(100vh-105px)] lg:grid-cols-1">
            {quickPrompts.map(item => {
              const Icon = item.icon;

              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => handleSend(item.text, user && item.showPerformanceReport ? '' : item.answer)}
                  className="min-h-11 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left hover:border-blue-300 hover:bg-blue-50 transition"
                >
                <span className="flex items-center gap-2 text-xs font-black text-slate-800">
                  <Icon size={14} className="text-blue-600 shrink-0" />
                  {item.label}
                </span>
                </button>
              );
            })}
          </div>

        </aside>

        <section className="flex min-h-[calc(100vh-245px)] flex-col bg-white lg:min-h-[calc(100vh-73px)]">
          <div className="flex-1 overflow-y-auto px-3 py-4 sm:px-6 sm:py-6">
            <div className="mx-auto max-w-3xl space-y-4">
              {messages.map((message, index) => (
                <div
                  key={message.id || index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`${message.performanceReport || message.comparisonReport || message.resourceSuggestions ? 'max-w-[96%] sm:max-w-[92%] lg:max-w-[860px]' : 'max-w-[88%]'} whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
                      message.role === 'user'
                        ? 'bg-blue-600 text-white rounded-br-md'
                        : 'bg-slate-100 text-slate-800 rounded-bl-md'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <span className="mb-1 flex items-center gap-2 text-xs font-black text-blue-700">
                        <Bot size={14} />
                        Eascode
                      </span>
                    )}
                    {message.text}
                    {message.role === 'assistant' && getYoutubeEmbedUrl(message.text) && (
                      <iframe
                        className="mt-3 aspect-video w-full rounded-xl border border-slate-200 bg-black"
                        src={getYoutubeEmbedUrl(message.text)}
                        title="Eascode help video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    )}
                    {message.performanceReport && (
                      <PerformanceReportCard
                        report={message.performanceReport}
                        onResourceRequest={handleResourceRequest}
                        resourceLoading={resourceLoading}
                      />
                    )}
                    {message.comparisonReport && (
                      <CrossSchoolComparisonCard report={message.comparisonReport} />
                    )}
                    {message.resourceSuggestions && (
                      <ResourceSuggestionCard
                        suggestions={message.resourceSuggestions}
                        onWorksheetDownload={handleWorksheetDownload}
                        onQuizOpen={handleQuizOpen}
                      />
                    )}
                  </div>
                </div>
              ))}

              {sending && (
                <div className="flex justify-start">
                  <div className="inline-flex items-center gap-2 rounded-2xl rounded-bl-md bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-500">
                    <Loader2 className="animate-spin" size={16} />
                    Thinking...
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white p-3 sm:p-4">
            <div className="mx-auto flex max-w-3xl gap-2">
              <textarea
                value={input}
                onChange={event => setInput(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handleSend();
                  }
                }}
                rows={2}
                className="min-h-12 flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500 disabled:bg-slate-100"
                placeholder={user ? 'Ask Eascode...' : 'Ask in guest mode...'}
                disabled={sending}
              />
              <button
                type="button"
                onClick={() => handleSend()}
                disabled={sending || !input.trim()}
                className="h-12 w-12 shrink-0 rounded-xl bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 disabled:opacity-50 transition"
                title="Send"
                aria-label="Send"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default EascodeChat;
